import {
  ScrollableCurriculumTabStrip,
  type ScrollableCurriculumTabStripHandle,
} from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { CurriculumParentStripNavSyncContext } from "@/contexts/CurriculumParentStripNavSyncContext";
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BeltRank } from "../../MembersArea";
import type { DaeMahngChunPattern } from "./DaeMahngChun";
import type { DaeMahngJiPattern } from "../../components/types";
import Choshim from "./Choshim";
import DaeMahngChun from "./DaeMahngChun";
import DaeMahngJi from "./DaeMahngJi";
import GyeomJeon from "./GyeomJeon";
import JoHwa from "./JoHwa";
import PalBahngMooGong from "./PalBahngMooGong";
import SaengMyongEum from "./SaengMyongEum";
import SaengMyongYang from "./SaengMyongYang";
import SongMoo from "./SongMoo";

type Section = { id: string; label: string };

const DEFAULT_SURFACE = "!bg-gray-200 !text-gray-900";

export interface CurriculumMooGongRyuProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  beltTabSurfaceClass: string;
  hasDemo: (aliases: string[]) => boolean;
  onWatchDemo: (aliases: string[]) => void;
  showDaeMahngChun: boolean;
  showDaeMahngJi: boolean;
  showChoshim: boolean;
  showJoHwa: boolean;
  showGyeomJeon: boolean;
  showPalBahngMooGong: boolean;
  showSongMoo: boolean;
  showSaengMyongEum: boolean;
  showSaengMyongYang: boolean;
  dmcPatterns: DaeMahngChunPattern[];
  currentDMC: number;
  dmjPatterns: DaeMahngJiPattern[];
  currentDMJ: number;
}

export default function CurriculumMooGongRyu({
  selectedBelt,
  getBeltColor,
  beltTabSurfaceClass,
  hasDemo,
  onWatchDemo,
  showDaeMahngChun,
  showDaeMahngJi,
  showChoshim,
  showJoHwa,
  showGyeomJeon,
  showPalBahngMooGong,
  showSongMoo,
  showSaengMyongEum,
  showSaengMyongYang,
  dmcPatterns,
  currentDMC,
  dmjPatterns,
  currentDMJ,
}: CurriculumMooGongRyuProps) {
  const sections: Section[] = useMemo(() => {
    const rows: Section[] = [];
    if (showDaeMahngChun) rows.push({ id: "dmc", label: "DaeMahngChun" });
    if (showDaeMahngJi) rows.push({ id: "dmj", label: "DaeMahngJi" });
    if (showChoshim) rows.push({ id: "choshim", label: "Choshim" });
    if (showJoHwa) rows.push({ id: "johwa", label: "JoHwa" });
    if (showGyeomJeon) rows.push({ id: "gyeomjeon", label: "GyeomJeon" });
    if (showPalBahngMooGong) rows.push({ id: "palbahng", label: "PalBahng Moo Gong" });
    if (showSongMoo) rows.push({ id: "songmoo", label: "SongMoo" });
    if (showSaengMyongEum) rows.push({ id: "saeng-eum", label: "SaengMyongEum" });
    if (showSaengMyongYang) rows.push({ id: "saeng-yang", label: "SaengMyongYang" });
    return rows;
  }, [
    showDaeMahngChun,
    showDaeMahngJi,
    showChoshim,
    showJoHwa,
    showGyeomJeon,
    showPalBahngMooGong,
    showSongMoo,
    showSaengMyongEum,
    showSaengMyongYang,
  ]);

  const fingerprint = sections.map((s) => s.id).join("|");
  const [mainTab, setMainTab] = useState(() => sections[0]?.id ?? "dmc");

  useEffect(() => {
    if (!sections.some((s) => s.id === mainTab)) {
      setMainTab(sections[0]?.id ?? "");
    }
  }, [fingerprint, mainTab, sections]);

  const surface = beltTabSurfaceClass ?? DEFAULT_SURFACE;
  const mgrStripRef = useRef<ScrollableCurriculumTabStripHandle>(null);
  const scrollMooGongRyuOrchestratorStrip = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      mgrStripRef.current?.getScrollContainer() ?? null,
    );
  }, []);

  if (sections.length === 0) return null;

  return (
    <div className="space-y-4 text-left">
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full space-y-4">
        <ScrollableCurriculumTabStrip ref={mgrStripRef} key={fingerprint} ariaLabelPrefix="Moo Gong Ryu">
          {sections.map((s) => (
            <TabsTrigger
              key={s.id}
              value={s.id}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium transition-shadow",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              {s.label}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>

        <CurriculumParentStripNavSyncContext.Provider value={scrollMooGongRyuOrchestratorStrip}>
        {showDaeMahngChun && (
          <TabsContent value="dmc" className="mt-0 focus-visible:outline-none">
            <DaeMahngChun
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              patterns={dmcPatterns}
              currentPattern={currentDMC}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showDaeMahngJi && (
          <TabsContent value="dmj" className="mt-0 focus-visible:outline-none">
            <DaeMahngJi
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              patterns={dmjPatterns}
              currentPattern={currentDMJ}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showChoshim && (
          <TabsContent value="choshim" className="mt-0 focus-visible:outline-none">
            <Choshim
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
            />
          </TabsContent>
        )}

        {showJoHwa && (
          <TabsContent value="johwa" className="mt-0 focus-visible:outline-none">
            <JoHwa
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
            />
          </TabsContent>
        )}

        {showGyeomJeon && (
          <TabsContent value="gyeomjeon" className="mt-0 focus-visible:outline-none">
            <GyeomJeon
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showPalBahngMooGong && (
          <TabsContent value="palbahng" className="mt-0 focus-visible:outline-none">
            <PalBahngMooGong
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showSongMoo && (
          <TabsContent value="songmoo" className="mt-0 focus-visible:outline-none">
            <SongMoo
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showSaengMyongEum && (
          <TabsContent value="saeng-eum" className="mt-0 focus-visible:outline-none">
            <SaengMyongEum
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}

        {showSaengMyongYang && (
          <TabsContent value="saeng-yang" className="mt-0 focus-visible:outline-none">
            <SaengMyongYang
              selectedBelt={selectedBelt}
              getBeltColor={getBeltColor}
              presentation="curriculum"
              beltTabSurfaceClass={surface}
            />
          </TabsContent>
        )}
        </CurriculumParentStripNavSyncContext.Provider>
      </Tabs>
    </div>
  );
}
